// Single API client for the EveryPlace onboarding app.
//
// Single-origin model (IMPLEMENTATION-BRIEF §0): the React app and the Express
// API are served from the SAME origin (onboard.everyplace.com). The API lives under
// /api, so apiBase is just the relative string "/api" in dev and prod alike —
// there is no cross-origin URL and no CORS. The session is a first-party,
// same-origin HttpOnly cookie (ep_session / ep_admin), so every request sends
// credentials: "include".

export const apiBase = "/api";

export class ApiError extends Error {
  constructor(message, { status, code, body } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

async function request(method, path, { body, headers, signal } = {}) {
  const res = await fetch(apiBase + path, {
    method,
    credentials: "include", // first-party session cookie travels with every call
    headers: {
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });

  // 204 / empty body
  const text = await res.text();
  let data = null;
  if (text) {
    try { data = JSON.parse(text); } catch { data = text; }
  }

  if (!res.ok) {
    const message = (data && data.error) || res.statusText || "Request failed";
    throw new ApiError(message, {
      status: res.status,
      code: data && data.code,
      body: data,
    });
  }
  return data;
}

export const api = {
  get: (path, opts) => request("GET", path, opts),
  post: (path, body, opts) => request("POST", path, { ...opts, body }),
  put: (path, body, opts) => request("PUT", path, { ...opts, body }),
  patch: (path, body, opts) => request("PATCH", path, { ...opts, body }),
  del: (path, opts) => request("DELETE", path, opts),
};

// Generate a client-side idempotency key (UUID) for a mutating action, so a
// double-tap / retry of "Send for signature", escrow create, etc. is de-duped
// server-side (IMPLEMENTATION-BRIEF §7b-1). Pass it through the Idempotency-Key
// header on the relevant POST.
export function idempotencyKey() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  // Fallback for older browsers
  return "idem-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
}

export default api;
